import { createPortal } from 'react-dom';
import { useState } from 'react';
import PropTypes from 'prop-types';
import { readerApi } from '../../services/api';
import EditReaderForm from './EditReaderForm';
import './ReaderCard.css';

const ReaderCard = ({ reader, onUpdate, onDelete, isPrimary }) => {
  const [showEditForm, setShowEditForm] = useState(false);
  const [showProgress, setShowProgress] = useState(false);
  const [progress, setProgress] = useState(null);
  const [loadingProgress, setLoadingProgress] = useState(false);

  const handleEditSave = async (updatedData) => {
    try {
      const response = await readerApi.updateReader(reader.id, updatedData);
      onUpdate(response.data.reader);
      setShowEditForm(false);
    } catch (err) {
      console.error('Error updating reader:', err);
      throw err;
    }
  };

  const handleDelete = () => {
    onDelete(reader.id);
  };

  const loadProgress = async () => {
    if (progress) {
      setShowProgress(!showProgress);
      return;
    }

    try {
      setLoadingProgress(true);
      const response = await readerApi.getReaderProgress(reader.id);
      setProgress(response.data.progress);
      setShowProgress(true);
    } catch (err) {
      console.error('Error loading progress:', err);
    } finally {
      setLoadingProgress(false);
    }
  };

  const formatAge = (age) => {
    if (!age) return 'Age not specified';
    return `${age} year${age !== 1 ? 's' : ''} old`;
  };

  const formatGender = (gender) => {
    if (!gender || gender === 'prefer_not_to_say') return 'Gender not specified';
    return gender.charAt(0).toUpperCase() + gender.slice(1);
  };

  return (
    <div className={`reader-card ${isPrimary ? 'primary-reader' : ''}`}>
      <div className="reader-card-header">
        <h4 className="reader-name">{reader.name}</h4>
        {isPrimary && (
          <span className="primary-badge">Primary</span>
        )}
      </div>

      <div className="reader-details">
        <div className="reader-info">
          <span className="info-label">Age:</span>
          <span className="info-value">{formatAge(reader.age)}</span>
        </div>
        <div className="reader-info">
          <span className="info-label">Gender:</span>
          <span className="info-value">{formatGender(reader.gender)}</span>
        </div>
      </div>

      {progress && showProgress && (
        <div className="progress-section">
          <h5>Progress</h5>
          <div className="progress-stats">
            <div className="progress-stat">
              <span className="stat-number">{progress.totalChaptersRead}</span>
              <span className="stat-label">Chapters logged</span>
            </div>
            <div className="progress-stat">
              <span className="stat-number">{progress.uniqueChaptersRead}</span>
              <span className="stat-label">Unique chapters</span>
            </div>
          </div>
          <div className="testament-stats">
            <div className="testament-stat">
              <span className="testament-label">Old Testament:</span>
              <span className="testament-value">{progress.oldTestamentChapters}</span>
            </div>
            <div className="testament-stat">
              <span className="testament-label">New Testament:</span>
              <span className="testament-value">{progress.newTestamentChapters}</span>
            </div>
          </div>
        </div>
      )}

      <div className="reader-actions">
        <button 
          className="btn btn-small btn-secondary"
          onClick={loadProgress}
          disabled={loadingProgress}
        >
          {loadingProgress ? 'Loading...' : 
           showProgress ? 'Hide progress' : 'Show progress'}
        </button>
        
        <button 
          className="btn btn-small btn-outline"
          onClick={() => setShowEditForm(true)}
        >
          Edit
        </button>
        
        {!isPrimary && (
          <button 
            className="btn btn-small btn-danger"
            onClick={handleDelete}
          >
            Delete
          </button>
        )}
      </div>

      {/* Edit Form Modal */}
      {showEditForm && createPortal(
        <div className="modal-overlay">
          <div className="modal-content">
            <EditReaderForm
              reader={reader}
              onSave={handleEditSave}
              onCancel={() => setShowEditForm(false)}
              isPrimary={isPrimary}
            />
          </div>
        </div>,
        document.body
      )}
    </div>
  );
};

ReaderCard.propTypes = {
  reader: PropTypes.shape({
    id: PropTypes.string.isRequired,
    name: PropTypes.string.isRequired,
    age: PropTypes.number,
    gender: PropTypes.string,
    isPrimary: PropTypes.bool.isRequired,
  }).isRequired,
  onUpdate: PropTypes.func.isRequired,
  onDelete: PropTypes.func.isRequired,
  isPrimary: PropTypes.bool.isRequired,
};

export default ReaderCard;
